<template>
  <div class="w-full lg:w-2/5">
    <div class="relative overflow-hidden rounded-lg border">
      <img class="mb-2" :src="getImageUrl" :alt="bookedRoom?.content?.name" />

      <div class="space-y-3 p-2">
        <div>
          <h3 class="text-lg font-bold">{{ bookedRoom?.content?.name }}</h3>
          <div class="text-sm text-stone-700 flex">
        <span v-if="bookedRoom?.content?.ratings?.property?.rating">
            {{ bookedRoom?.content?.ratings?.property?.rating }} {{ " " }}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="w-5 h-5 text-yellow-600 inline-block"
            >
              <path
                fill-rule="evenodd"
                d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"
                clip-rule="evenodd"
              ></path>
            </svg>
              {{ " - " }}
            </span>
            {{ getFullAdress }}
          </div>
        </div>

        <div class="text-sm" v-if="bookedRoom?.meal">
          <div class="mb-1 font-bold">Meal:</div>
          <div>{{ bookedRoom?.meal }}</div>
        </div>

        <div class="text-sm">
          <div class="mb-1 font-bold">Check-in:</div>
          <div>{{ getCheckInTime }}</div>
        </div>

        <div class="text-sm">
          <div class="mb-1 font-bold">Check-out:</div>
          <div>{{ getCheckOutTime }}</div>
        </div>

        <div class="text-sm">
          <div class="mb-1 font-bold">Cancellation:</div>
          <!-- R2B format - cancellation_policy is a string -->
          <template v-if="typeof bookedRoom?.content?.cancellation_policy === 'string'">
            <div class="whitespace-pre-wrap">
              {{ bookedRoom?.content?.cancellation_policy }}
            </div>
            <div class="whitespace-pre-wrap mt-2">
              {{ hardCodedPolicy }}
            </div>
          </template>
          <!-- Regular format - cancellation_policy is an object -->
          <template v-else>
          <li v-if="bookedRoom?.content?.cancellation_policy?.free?.length">
            {{ bookedRoom?.content?.cancellation_policy?.free }}
          </li>
          <li
            v-if="
              bookedRoom?.content?.cancellation_policy?.norefund_after?.length
            "
          >
            {{ bookedRoom?.content?.cancellation_policy?.norefund_after }}
          </li>
          <li v-if="bookedRoom?.content?.cancellation_policy?.norefund?.length">
            <span
              v-html="bookedRoom?.content?.cancellation_policy?.norefund"
            ></span>
          </li>
          <li v-if="bookedRoom?.content?.cancellation_policy?.partial?.length">
            <span
              v-html="bookedRoom?.content?.cancellation_policy?.partial"
            ></span>
          </li>
          <li v-if="bookedRoom?.content?.cancellation_policy?.terms?.length">
            <span
              v-html="bookedRoom?.content?.cancellation_policy?.terms"
            ></span>
          </li>
        </template>
        </div>

        <div class="text-sm">
          <strong>{{ getNightsStay }}</strong>
          nights stay
        </div>

      </div>
    </div>
  </div>
</template>
<script setup>
import { computed } from "vue";
import { imageFunction } from "../../utils/imageFunction.js";
import { API_URL } from "../../src/constants.js";

const props = defineProps({
  bookedRoom: {
    type: Object,
    required: true,
  },
});

// Get dynamic hard-coded policy from API or fallback to default
const hardCodedPolicy = computed(() => {
  return props.bookedRoom?.content?.hard_coded_policy || 
    "Full refunds are subject to payment gateway and refund fees of 1.6% + $0.60 for Australian debit and credit cards and 3.4% + $0.60 for international debit and credit cards.";
});
const getFullAdress = computed(() => {
  // Check if address is a string (R2B format) or object (regular format)
  if (typeof props.bookedRoom?.content?.address === 'string') {
    // R2B format - address is already a string
    return props.bookedRoom.content.address;
  }
  
  // Regular format - address is an object
  return `${
    props.bookedRoom?.content?.address?.line_1 !== null
      ? props.bookedRoom?.content?.address?.line_1
      : ""
  }, ${
    props.bookedRoom?.content?.address?.city !== null
      ? props.bookedRoom?.content?.address?.city
      : ""
  }, ${
    props.bookedRoom?.content?.address?.state_province_name !== null
      ? props.bookedRoom?.content?.address?.state_province_name
      : ""
  }, ${
    props.bookedRoom?.content?.address?.postal_code !== null
      ? props.bookedRoom?.content?.address?.postal_code
      : ""
  } `;
});
const getCheckInTime = computed(() => {
  if (!props.bookedRoom.checkin) return ""; // Handle empty case

  const date = new Date(props.bookedRoom.checkin);

  // Get formatted date: "Wednesday, 12th of March 2025"
  const weekday = new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(
    date
  );
  const day = date.getDate();
  const suffix = ["th", "st", "nd", "rd"][
    day % 10 > 3 || Math.floor((day % 100) / 10) === 1 ? 0 : day % 10
  ];
  const month = new Intl.DateTimeFormat("en-US", { month: "long" }).format(
    date
  );
  const year = date.getFullYear();

  return `${weekday}, ${day}${suffix} of ${month} ${year}`;
});
const getCheckOutTime = computed(() => {
  if (!props.bookedRoom.checkout) return "";

  const date = new Date(props.bookedRoom.checkout);

  const weekday = new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(
    date
  );
  const day = date.getDate();
  const suffix = ["th", "st", "nd", "rd"][
    day % 10 > 3 || Math.floor((day % 100) / 10) === 1 ? 0 : day % 10
  ];
  const month = new Intl.DateTimeFormat("en-US", { month: "long" }).format(
    date
  );
  const year = date.getFullYear();

  return `${weekday}, ${day}${suffix} of ${month} ${year}`;
});

const getImageUrl = computed(() => {
  const image = imageFunction(props.bookedRoom?.content?.images);
  return image[0];
});

const getNightsStay = computed(() => {
  if (!props.bookedRoom.checkin || !props.bookedRoom.checkout) return 0;

  const checkinDate = new Date(props.bookedRoom.checkin);
  const checkoutDate = new Date(props.bookedRoom.checkout);

  // Calculate the difference in milliseconds and convert to days
  const diffInTime = checkoutDate - checkinDate;
  const diffInDays = diffInTime / (1000 * 60 * 60 * 24);

  return diffInDays;
});

</script>
